/**
 * Server-secret discovery overlay. The shapes and validators are shared so a
 * browser can consume a decrypted manifest, but manifest generation and key
 * derivation deliberately live in the server package.
 */
import type { ResourceNode } from "./resources.js";
import * as pmath from "./pmath.js";

export const HIDDEN_MANIFEST_VERSION = 1 as const;
/** Physical-world contract; v21 retains dry waterfall bank shelves in shared terrain. */
export const WORLD_GENERATION_VERSION = 22 as const;
export const HIDDEN_RESOURCE_ID_BASE = 100_000;

export interface HiddenResourceSiteV1 {
  id: number;
  planet: number;
  dir: [number, number, number];
  kind: "volatite" | "ferrite" | "cryonite";
  capacity: number;
}

export interface HiddenSystemManifestV1 {
  v: typeof HIDDEN_MANIFEST_VERSION;
  systemId: string;
  generatedBy: "server-secret-v1";
  resources: HiddenResourceSiteV1[];
}

export interface SystemRevealV1 {
  systemId: string;
  generationVersion: typeof WORLD_GENERATION_VERSION;
  keyVersion: 1;
  /** Present only for a sealed:v2 system. Base64url, 32 bytes. */
  worldSeed?: string;
  /** AES-GCM key for the public encrypted discovery manifest. */
  manifestKey: string;
  manifestUrl: string;
  /** Short-lived, server-authenticated admission proof for the reconnect
   *  that follows a warp into a sealed system. */
  access?: string;
}

function finiteVec3(v: unknown): v is [number, number, number] {
  return Array.isArray(v) && v.length === 3 && v.every(Number.isFinite);
}

export function validateHiddenSystemManifest(raw: unknown, expectedSystem?: string): HiddenSystemManifestV1 | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o.v !== HIDDEN_MANIFEST_VERSION || o.generatedBy !== "server-secret-v1"
    || typeof o.systemId !== "string" || (expectedSystem !== undefined && o.systemId !== expectedSystem)
    || !Array.isArray(o.resources) || o.resources.length > 64) return null;
  const resources: HiddenResourceSiteV1[] = [];
  const ids = new Set<number>();
  for (const rawSite of o.resources) {
    if (!rawSite || typeof rawSite !== "object" || Array.isArray(rawSite)) return null;
    const site = rawSite as Record<string, unknown>;
    if (!Number.isInteger(site.id) || (site.id as number) < HIDDEN_RESOURCE_ID_BASE || ids.has(site.id as number)
      || !Number.isInteger(site.planet) || (site.planet as number) < 0 || (site.planet as number) > 64
      || !finiteVec3(site.dir)
      || (site.kind !== "volatite" && site.kind !== "ferrite" && site.kind !== "cryonite")
      || !Number.isInteger(site.capacity) || (site.capacity as number) < 1 || (site.capacity as number) > 1000) return null;
    const len = pmath.hypot(site.dir[0], site.dir[1], site.dir[2]);
    if (Math.abs(len - 1) > 1e-6) return null;
    ids.add(site.id as number);
    resources.push({
      id: site.id as number,
      planet: site.planet as number,
      dir: [site.dir[0], site.dir[1], site.dir[2]],
      kind: site.kind,
      capacity: site.capacity as number,
    });
  }
  return { v: 1, systemId: o.systemId, generatedBy: "server-secret-v1", resources };
}

const MANIFESTS = new Map<string, HiddenSystemManifestV1>();
const MANIFEST_CACHE_MAX = 256;

export function registerHiddenSystemManifest(manifest: HiddenSystemManifestV1): void {
  MANIFESTS.delete(manifest.systemId);
  MANIFESTS.set(manifest.systemId, manifest);
  while (MANIFESTS.size > MANIFEST_CACHE_MAX) {
    const oldest = MANIFESTS.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    MANIFESTS.delete(oldest);
  }
}

export function hiddenSystemManifest(systemId: string): HiddenSystemManifestV1 | null {
  return MANIFESTS.get(systemId) ?? null;
}

export function hiddenResourceNodes(manifest: HiddenSystemManifestV1 | null | undefined): ResourceNode[] {
  return manifest?.resources.map((site) => ({ ...site, dir: [...site.dir] as [number, number, number] })) ?? [];
}
